/**
 * A bounded wait for the canvas saves already underway.
 *
 * Task 0112: `e2e/fabric-row.mjs` sat inside one `page.evaluate` for more than
 * ten minutes and had to be killed. It did not fail, did not time out and did
 * not print anything further. The read it was blocked in begins with a wait for
 * quiescence, and that wait was a spin with no ceiling:
 *
 * ```js
 * while (documentSaveInFlight > 0) {
 *   await new Promise((resolve) => { setTimeout(resolve, 0); });
 * }
 * ```
 *
 * `saveDocument` is queued per document, so ONE entry that never settles blocks
 * every later save for that document and therefore every canonical read of the
 * whole page, forever.
 *
 * Two things follow, and the second is the one this module is for.
 *
 * - **A blocked canonical read is silent.** The page does not report a stuck
 *   save, does not fall back, and does not fail. A person driving the product
 *   sees a surface that has simply stopped answering — which is worse than an
 *   error, because there is nothing to read.
 * - **CI cannot tell you which one it was.** The row harnesses share one job. A
 *   hang consumes the budget and every step after it is reported as cancelled,
 *   so the run says "the gate timed out" rather than "a canonical read blocked
 *   in the Fabric row".
 *
 * So the wait gives up, and SAYS so. It does not pretend the save finished: the
 * report names how long it waited and how many saves are still outstanding, and
 * an abandonment is remembered so a harness can ask about it afterwards rather
 * than having to observe the moment it happened.
 *
 * The ceiling belongs to the EPISODE of unfinished work, not to one call. A
 * per-call deadline does not fix this defect: `storedRole` awaits quiescence,
 * and the harness that hung makes 64 serial `storedRole` calls inside one
 * `page.evaluate`, so 64 × 10s is the ten minutes back. See `episodeStartedAt`.
 *
 * What this is deliberately NOT: a fix for the hang. 0112 records one hang in
 * four runs and its root cause is still an inference. A ceiling turns ten silent
 * minutes into one legible line; it does not make a stuck save finish.
 */

/** Why a wait ended early, and with what still outstanding. */
export interface QuiescenceReport {
  /** Wall-clock milliseconds spent waiting before giving up. */
  readonly waitedMs: number;
  /** Saves still unfinished at the moment the wait was abandoned. */
  readonly inFlight: number;
  /** The ceiling that was crossed. */
  readonly ceilingMs: number;
}

export interface QuiescenceOptions {
  /**
   * How long a canonical read will wait for outstanding saves.
   *
   * Three orders of magnitude above a normal IndexedDB canvas save, and well
   * inside a single harness step, so an abandonment lands as one failing
   * assertion in the journey that provoked it rather than as a job timeout
   * naming no step at all.
   */
  readonly ceilingMs?: number;
  /** Wall clock. Injected so a test does not have to spend the ceiling. */
  readonly now?: () => number;
  /**
   * One turn of the event loop. The default is the `setTimeout(…, 0)` the spin
   * always used — a save settles on a later task, so a microtask yield here
   * would spin without ever letting one land.
   */
  readonly nextTurn?: () => Promise<void>;
  /** Called once per abandoned wait, for the visible notice. */
  readonly onAbandon?: (report: QuiescenceReport) => void;
}

export const DEFAULT_CEILING_MS = 10_000;

export interface Quiescence {
  /** Count `work` as an in-flight save for as long as it runs. */
  track: <T>(work: () => Promise<T>) => Promise<T>;
  /** Saves running right now. */
  readonly inFlight: number;
  /**
   * Resolve when nothing is in flight, or at the ceiling.
   *
   * `undefined` means quiet. A report means the wait was ABANDONED and whatever
   * the caller reads next may be mid-flight — which is the honest answer, and
   * the one the spin could never give.
   */
  settled: () => Promise<QuiescenceReport | undefined>;
  /** The most recent abandonment, for a harness asking after the fact. */
  readonly lastAbandon: QuiescenceReport | undefined;
  /** How many waits have been abandoned since the page opened. */
  readonly abandonCount: number;
}

export function createQuiescence(options: QuiescenceOptions = {}): Quiescence {
  const ceilingMs = options.ceilingMs ?? DEFAULT_CEILING_MS;
  /**
   * MONOTONIC by default. @Codex, P2 on #184: *"When the system wall clock
   * moves backward while a save is outstanding — for example after a manual
   * clock correction or VM time synchronization — `Date.now() - started`
   * remains below the ceiling for the size of that adjustment, so a canonical
   * read intended to give up after 10 seconds can block for minutes again."*
   *
   * The whole point of this file is that a wait cannot last forever, and
   * `Date.now()` is a value that can be moved by something outside the program.
   * `performance.now()` cannot. The injected clock is untouched, because every
   * test drives a fake one and a fake clock has no wall time to correct.
   */
  const now = options.now
    ?? (typeof performance === 'object' && typeof performance.now === 'function'
      ? () => performance.now()
      : () => Date.now());
  const nextTurn = options.nextTurn
    ?? (() => new Promise<void>((resolve) => { setTimeout(resolve, 0); }));

  let inFlight = 0;
  /**
   * When the CURRENT episode of unfinished work began, and whether it has
   * already been given up on.
   *
   * The ceiling belongs to the episode rather than to the call, and that is not
   * a refinement — a per-call deadline does not fix the defect it was written
   * for. @Codex, P1 on #184: *"`started` is reset on every `settled()` call
   * while the same save remains in flight, so consecutive canonical reads each
   * spend another full 10-second ceiling. `e2e/fabric-row.mjs:347-354` performs
   * up to 64 serial `storedRole()` calls inside one `page.evaluate`, so one
   * stuck save can still hold that evaluation for roughly 640 seconds and
   * reproduce the reported ten-minute silence."*
   *
   * 64 × 10s is the ten minutes, almost exactly. So the episode is the unit:
   * every read taken while the same work is outstanding shares one budget, and
   * once it is spent the reads after it return the standing report at once
   * rather than each buying another ceiling.
   */
  let episodeStartedAt: number | undefined;
  /**
   * Which episode this is, counted.
   *
   * @Codex, P2 on #184: *"If the original save becomes quiet and a new save
   * starts before this waiting loop gets its next turn, `episodeStartedAt` is
   * reset for the new episode but this local `started` still names the old one.
   * A read that waited nearly ten seconds for the first save can consequently
   * abandon the new episode after only milliseconds and record a misleading
   * report."*
   *
   * The deadline moved from the call to the episode and the loop kept holding
   * the episode by its START TIME, which is a value two episodes can disagree
   * about while the loop is not looking. A counter is the identity; the time is
   * a property of it.
   */
  let episode = 0;
  let episodeAbandon: QuiescenceReport | undefined;
  let lastAbandon: QuiescenceReport | undefined;
  let abandonCount = 0;

  return {
    async track<T>(work: () => Promise<T>): Promise<T> {
      // The episode opens on the FIRST outstanding save and does not restart
      // for later ones. A page that saves continuously must not be able to
      // push the deadline ahead of itself forever.
      if (inFlight === 0) { episodeStartedAt = now(); episode += 1; }
      inFlight += 1;
      // `finally`, not a trailing decrement: a save that THROWS must not leave
      // the counter raised, or the first failed write hangs every read after
      // it — the defect this module exists for, arriving by the shorter route.
      try {
        return await work();
      } finally {
        inFlight -= 1;
        // Quiet again: the episode is over, and the next one gets a fresh
        // budget and may report again. A stuck save that eventually lands must
        // not leave the page permanently refusing to wait.
        if (inFlight === 0) { episodeStartedAt = undefined; episodeAbandon = undefined; }
      }
    },
    get inFlight() { return inFlight; },
    async settled(): Promise<QuiescenceReport | undefined> {
      if (inFlight === 0) return undefined;
      // Already given up on this episode. Returned WITHOUT notifying again:
      // `say()` clears its predecessor, so 64 reads of one stuck save would
      // otherwise overwrite the notice 64 times and leave a person watching a
      // message flicker instead of reading it.
      if (episodeAbandon) return episodeAbandon;
      // Read the clock before the first check, so an episode that is already
      // over the ceiling when this is called cannot be reported as instant.
      let started = episodeStartedAt ?? now();
      let waitingFor = episode;
      while (inFlight > 0) {
        // The episode this loop is timing may have ENDED and been replaced
        // while it was not looking — see `episode`. Re-reading the deadline is
        // what stops a read that waited nine seconds for the first save from
        // abandoning the second one after a millisecond and reporting it.
        if (episode !== waitingFor) {
          waitingFor = episode;
          started = episodeStartedAt ?? now();
        }
        // Re-checked INSIDE the loop, because the check above happened before
        // an `await`. @Codex, P2 on #184: *"If two canonical reads call
        // `settled()` concurrently during one stuck save, both can pass the
        // initial `episodeAbandon` check before yielding and later enter this
        // deadline branch. Each call then increments `abandonCount` and invokes
        // `onAbandon` for the same episode, contradicting the API's
        // episode-based count and producing duplicate notices."*
        //
        // Exactly the defect this whole file was rewritten to fix, one level
        // in: the first version counted per CALL and 64 serial reads spent 64
        // ceilings. Making the deadline per-episode fixed the serial case and
        // left the concurrent one, because a check before an await is a check
        // about a moment that has passed.
        if (episodeAbandon) return episodeAbandon;
        if (now() - started >= ceilingMs) {
          const report: QuiescenceReport = {
            waitedMs: now() - started, inFlight, ceilingMs,
          };
          episodeAbandon = report;
          lastAbandon = report;
          abandonCount += 1;
          options.onAbandon?.(report);
          return report;
        }
        await nextTurn();
      }
      return undefined;
    },
    get lastAbandon() { return lastAbandon; },
    /** Abandoned EPISODES, not abandoned reads. */
    get abandonCount() { return abandonCount; },
  };
}
